import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Popup } from 'semantic-ui-react';
import { FilterDropdownItemProps, NavigationItemDto } from 'types/types';
import { getDefaultFilters, getOptionsForLevel } from 'utils/helpers';

type FilterOptionsProps = {
  levelIndex: number;
  filters: NavigationItemDto[];
  filterNames: string[];
  selectedFilters: string[];
  setSelectedFilters: Dispatch<SetStateAction<string[]>>;
};

type FilterProps = FilterOptionsProps & {
  option: FilterDropdownItemProps;
};

export function FilterOptions({
  levelIndex,
  filters,
  filterNames,
  selectedFilters,
  setSelectedFilters,
}: FilterOptionsProps) {
  const optionsData = getOptionsForLevel(levelIndex, filters, filterNames, selectedFilters, false);

  return (
    <div className="filter-options">
      {optionsData.map((option, index) => (
        <Filter
          key={index}
          option={option}
          levelIndex={levelIndex}
          filterNames={filterNames}
          filters={filters}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
      ))}
    </div>
  );
}

function Filter({
  levelIndex,
  filters,
  filterNames,
  selectedFilters,
  setSelectedFilters,
  option,
}: FilterProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isToShowTooltip, setShowTooltip] = useState(false);
  const filterName = option.value as string;

  const handleTooltip = () => {
    setTimeout(() => {
      setShowTooltip(!(ref?.current && ref.current.clientWidth < ref.current.scrollWidth));
    }, 10);
  };

  const selectAFilter = () => {
    selectFilter(selectedFilters, levelIndex, filterName, filters, filterNames, setSelectedFilters);
  };

  const classNameWhenSelected =
    selectedFilters[levelIndex] && selectedFilters[levelIndex] === option.value
      ? 'selected-option'
      : '';

  useEffect(() => {
    handleTooltip();

    window.addEventListener('resize', handleTooltip);
    return () => {
      window.removeEventListener('resize', handleTooltip);
    };
  }, [selectedFilters, ref?.current?.clientWidth, ref?.current?.scrollWidth]);

  return (
    <Popup
      className="p-os-tooltip filter-tooltip"
      key={`option-${(option.id ?? '') !== '' ? option.id : option.value}`}
      size="mini"
      content={option.value}
      disabled={isToShowTooltip}
      trigger={
        <div
          id={option.id}
          className={classNameWhenSelected}
          onClick={() => selectAFilter()}
          onKeyDown={() => selectAFilter()}
          role="menuitem"
          tabIndex={0}
          ref={ref}
        >
          <span>{option.value}</span>
        </div>
      }
    />
  );
}

function selectFilter(
  selectedFilters: string[],
  level: number,
  filterName: string,
  filters: NavigationItemDto[],
  filterNames: string[],
  setSelectedFilters: Dispatch<SetStateAction<string[]>>
) {
  let newFilters = [...selectedFilters];
  newFilters[level] = filterName;
  newFilters = getDefaultFilters(filters, filterNames, newFilters, level).selectedOptions;
  setSelectedFilters(newFilters);
}
